import { type OperationVariables } from '@apollo/react-common';
import { useCallback, useRef } from 'react';
import { type GraphQLTaggedNode } from 'react-relay';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { type QueryInteropFetchPolicy } from '../mapFetchPolicyForQuery.ts';
import { useMemoObj } from '../useMemoObj.ts';

import { getApolloRelayCacheBridgeDisabledReason } from './cacheBridgeGate.ts';
import { syncQueryCacheBridge } from './queryCacheBridgeSync.ts';

type ApolloQueryCacheBridgeSyncArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	fetchPolicy?: QueryInteropFetchPolicy;
	useAggClient?: boolean;
	onCompleted?: (data: TData) => void;
	callerPackage?: string;
};

type ApolloQueryCacheBridgeObservedResult<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	data?: TData;
	error?: unknown;
	skip?: boolean;
	variables?: TVariables;
};

export function useApolloQueryCacheBridgeSync<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	query,
	variables,
	fetchPolicy,
	useAggClient,
	onCompleted,
	callerPackage,
}: ApolloQueryCacheBridgeSyncArgs<TData, TVariables>) {
	const isEnabled = getApolloRelayCacheBridgeDisabledReason() === null;
	const memoizedOptionVariables = useMemoObj<TVariables>(variables, callerPackage);
	const hasOptionVariables = variables !== undefined;
	const initialVariables = hasOptionVariables ? memoizedOptionVariables : undefined;
	const latestVariablesRef = useRef<TVariables | undefined>(initialVariables);
	const completedCacheSyncDataRef = useRef<TData | undefined>(undefined);
	const latestOptionsRef = useRef({
		query,
		fetchPolicy,
		useAggClient,
	});
	latestOptionsRef.current = {
		query,
		fetchPolicy,
		useAggClient,
	};

	const onOriginalCompleted = useStableCallback(onCompleted ?? function noop() {});

	const getVariables = useCallback(
		(resultVariables?: TVariables) => {
			return resultVariables ?? (hasOptionVariables ? memoizedOptionVariables : undefined);
		},
		[hasOptionVariables, memoizedOptionVariables],
	);

	const getLatestVariables = useCallback(() => latestVariablesRef.current, []);

	const updateLatestVariables = useCallback((nextVariables?: TVariables) => {
		latestVariablesRef.current = nextVariables;
	}, []);

	const onCompletedWithCacheBridge = useStableCallback(
		(completedData: TData, completedVariables?: TVariables) => {
			if (!isEnabled) {
				onOriginalCompleted(completedData);
				return;
			}

			const resolvedVariables = completedVariables ?? latestVariablesRef.current;
			updateLatestVariables(resolvedVariables);

			completedCacheSyncDataRef.current = completedData;
			const latestOptions = latestOptionsRef.current;

			// Apollo onCompleted is a success signal, so mirror before handing
			// control back to the caller's original callback.
			syncQueryCacheBridge({
				source: 'apollo',
				query: latestOptions.query,
				variables: resolvedVariables,
				data: completedData,
				fetchPolicy: latestOptions.fetchPolicy,
				useAggClient: latestOptions.useAggClient,
			});
			onOriginalCompleted(completedData);
		},
	);

	const syncObservedResult = useCallback(
		({
			data,
			error,
			skip,
			variables: resultVariables,
		}: ApolloQueryCacheBridgeObservedResult<TData, TVariables>) => {
			if (!isEnabled) {
				return;
			}

			const resolvedVariables = getVariables(resultVariables);
			updateLatestVariables(resolvedVariables);

			if (completedCacheSyncDataRef.current === data) {
				return;
			}

			const latestOptions = latestOptionsRef.current;
			// Observed hook results can represent skip/error/partial states; pass
			// those through so the bridge can decide whether syncing is safe.
			syncQueryCacheBridge({
				source: 'apollo',
				skip,
				query: latestOptions.query,
				variables: resolvedVariables,
				data,
				error,
				fetchPolicy: latestOptions.fetchPolicy,
				useAggClient: latestOptions.useAggClient,
			});
		},
		[getVariables, isEnabled, updateLatestVariables],
	);

	return {
		getLatestVariables,
		getVariables,
		isEnabled,
		onCompleted: onCompletedWithCacheBridge,
		syncObservedResult,
		updateLatestVariables,
	};
}
